// Turn raw OCR text into a list of items. This is the reliable core of the app:
// no network, no model, just careful text wrangling. The optional LLM parser
// (llm.ts) can replace this, but everything falls back here.

import type { Item, ParseResult } from "../models/types.js";

// Words that mean "this line is a summary/payment line, not an item you'd tick
// off." Matched on word boundaries so short tokens (cb, ec, iva) don't hit inside
// real item names.
const SKIP_WORDS = [
  "subtotal", "sub-total", "sous-total", "zwischensumme", "subtotaal",
  "tax", "vat", "tva", "iva", "mwst", "btw", "igic",
  "tip", "gratuity", "service", "trinkgeld", "pourboire", "propina", "coperto", "servizio",
  "total", "totaal", "totale", "gesamt", "summe", "somme", "importe", "montant",
  "cash", "change", "card", "carte", "cb", "ec", "visa", "mastercard", "amex",
  "maestro", "bancontact", "girocard", "especes?", "contant", "bar",
  "balance", "due", "paid", "payer", "rendu", "wechselgeld", "monnaie",
  "rounding", "arrondi", "afronding",
];
const SKIP_RE = new RegExp(`\\b(?:${SKIP_WORDS.join("|")})\\b`, "i");

// Words that mark a grand-total line we want to capture as detectedTotal.
// "otal" catches OCR-damaged TOTAL ("[OTAL", "IOTAL" read as "|OTAL").
const TOTAL_WORD_LIST = [
  "total", "totaal", "totale", "gesamt", "summe", "somme", "importe", "montant",
  "otal", "to\\s*pay", "zu\\s*zahlen", "te\\s*betalen",
];
const TOTAL_WORDS = new RegExp(`\\b(${TOTAL_WORD_LIST.join("|")})\\b`, "i");

// Lines that look total-ish but aren't the grand total: subtotals, and per-head
// share lines ("Importe por comensal"; comersal is a common OCR misread).
const SUBTOTAL_HINT = /\b(sub|sous|zwischen|tussen)|comen?sal|comersal|per\s*head|par\s*personne/i;

// A price: something ending in two decimals, with , or . as the separator, and
// optional thousands separators before it. Handles 12,50 / 1.234,56 / 1,234.56.
const PRICE_RE = /-?\d{1,3}(?:[.\s]\d{3})*[.,]\d{2}(?!\d)|-?\d+[.,]\d{2}(?!\d)/g;

// A leading quantity: "2 ", "2x", "2 x", "3×".
const QTY_RE = /^\s*(\d{1,3})\s*[x×*@]?\s+(?=\D)/;

const CURRENCIES: Record<string, string> = {
  "€": "€", eur: "€", "£": "£", gbp: "£", $: "$", usd: "$", chf: "CHF", kr: "kr",
};

/** Parse a European-or-otherwise price token into a number. */
export function parsePrice(token: string): number | null {
  let cleaned = token.replace(/[^\d.,-]/g, "").trim();
  if (!cleaned) return null;
  const hasDot = cleaned.includes(".");
  const hasComma = cleaned.includes(",");

  if (hasDot && hasComma) {
    // The rightmost separator is the decimal point; the other groups thousands.
    if (cleaned.lastIndexOf(",") > cleaned.lastIndexOf(".")) {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (hasComma) {
    // Only commas: decimal if exactly two trailing digits, else thousands.
    cleaned = /,\d{2}$/.test(cleaned)
      ? cleaned.replace(/\./g, "").replace(",", ".")
      : cleaned.replace(/,/g, "");
  } else {
    // Only dots (or none): a single trailing ".dd" is decimal; strip the rest.
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      const dec = parts.pop();
      cleaned = parts.join("") + "." + dec;
    }
  }
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

function detectCurrency(text: string): string {
  const counts: Record<string, number> = {};
  const currencyRe = /€|£|\$|\bEUR\b|\bGBP\b|\bUSD\b|\bCHF\b|\bkr\b/gi;
  let match: RegExpExecArray | null;
  while ((match = currencyRe.exec(text))) {
    const key = match[0].toLowerCase();
    const sym = CURRENCIES[key] ?? match[0];
    counts[sym] = (counts[sym] ?? 0) + 1;
  }
  const best = Object.entries(counts).sort((left, right) => right[1] - left[1])[0];
  return best ? best[0] : "€";
}

function looksLikeSkip(line: string): boolean {
  return SKIP_RE.test(line);
}

/** Does this line have at least one letter? Pure-number/punctuation lines are noise. */
function hasLetters(text: string): boolean {
  return /[a-zA-ZÀ-ÿ]/.test(text);
}

let counter = 0;
function makeId(): string {
  return `item-${Date.now().toString(36)}-${(counter++).toString(36)}`;
}

export function parseReceipt(text: string): ParseResult {
  const currency = detectCurrency(text);
  const lines = text
    .split(/\r?\n/)
    .map((rawLine) => rawLine.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  const items: Item[] = [];
  let detectedTotal: number | null = null;

  for (const line of lines) {
    const lower = line.toLowerCase();
    const prices = [...line.matchAll(PRICE_RE)].map((match) => match[0]);

    // Capture a grand total from summary lines.
    if (TOTAL_WORDS.test(lower) && !SUBTOTAL_HINT.test(lower) && prices.length) {
      const val = parsePrice(prices[prices.length - 1]);
      if (val != null && (detectedTotal == null || val > detectedTotal)) {
        detectedTotal = val;
      }
      continue;
    }

    // Skip other summary/footer lines outright.
    if (looksLikeSkip(lower)) continue;

    // No price and no letters → junk. No price but letters → maybe a header; skip.
    if (!prices.length) continue;
    if (!hasLetters(line)) continue;

    // Quantity from a leading count.
    const qtyMatch = line.match(QTY_RE);
    const qty = qtyMatch ? Number(qtyMatch[1]) : null;

    // Two prices usually mean "unit ... lineTotal"; one price is the line total.
    let unitPrice: number | null = null;
    let lineTotal: number | null = null;
    if (prices.length >= 2) {
      unitPrice = parsePrice(prices[0]);
      lineTotal = parsePrice(prices[prices.length - 1]);
    } else {
      lineTotal = parsePrice(prices[0]);
    }

    // Name = the line with quantity and every price stripped out.
    let name = line;
    if (qtyMatch) name = name.slice(qtyMatch[0].length);
    for (const price of prices) name = name.replace(price, " ");
    name = name
      .replace(/[€£$]|\bEUR\b|\bGBP\b|\bUSD\b/gi, " ")
      .replace(/[x×*@=]/g, " ")
      .replace(/\.{2,}/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (!name) continue; // a price with no describable name is probably a subtotal fragment

    items.push({
      id: makeId(),
      name,
      qty,
      unitPrice,
      lineTotal,
      selected: false,
      source: line,
    });
  }

  return { items, detectedTotal, currency, method: "heuristic" };
}
